import { useEffect, useState } from 'react';
import { galleriesAPI, eventsAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { ImageUpload } from '../../components/ui/image-upload';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Image, Video, X, ExternalLink } from 'lucide-react';

export const GalleriesAdminPage = () => {
  const [galleries, setGalleries] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGallery, setEditingGallery] = useState(null);
  const [filterEventId, setFilterEventId] = useState('all');
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    event_id: '',
    description: '',
    hero_image: '',
    images: [],
    videos: [],
  });
  const [videoInput, setVideoInput] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [galleriesRes, eventsRes] = await Promise.all([
        galleriesAPI.getAll(),
        eventsAPI.getAll(),
      ]);
      setGalleries(galleriesRes.data);
      setEvents(eventsRes.data);
    } catch (error) {
      toast.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      slug: '',
      event_id: '',
      description: '',
      hero_image: '',
      images: [],
      videos: [],
    });
    setEditingGallery(null);
    setVideoInput('');
  };

  const openEditDialog = (gallery) => {
    setEditingGallery(gallery);
    setFormData({
      title: gallery.title,
      slug: gallery.slug || '',
      event_id: gallery.event_id,
      description: gallery.description || '',
      hero_image: gallery.hero_image || '',
      images: gallery.images || [],
      videos: gallery.videos || [],
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingGallery) {
        await galleriesAPI.update(editingGallery.id, formData);
        toast.success('Gallery aggiornata');
      } else {
        await galleriesAPI.create(formData);
        toast.success('Gallery creata');
      }
      setDialogOpen(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questa gallery?')) return;
    try {
      await galleriesAPI.delete(id);
      toast.success('Gallery eliminata');
      loadData();
    } catch (error) {
      toast.error('Errore eliminazione');
    }
  };

  // Image management
  const addImage = (url) => {
    setFormData(prev => ({
      ...prev,
      images: [...prev.images, { url, caption: '' }]
    }));
  };

  const updateImageCaption = (index, caption) => {
    const newImages = [...formData.images];
    newImages[index] = { ...newImages[index], caption };
    setFormData(prev => ({ ...prev, images: newImages }));
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  // Video management
  const addVideo = () => {
    if (!videoInput.trim()) return;
    
    // Detect if it's a YouTube URL
    const isYoutube = videoInput.includes('youtube.com') || videoInput.includes('youtu.be');
    
    setFormData(prev => ({
      ...prev,
      videos: [...prev.videos, { 
        url: videoInput.trim(), 
        type: isYoutube ? 'youtube' : 'upload',
        caption: '' 
      }]
    }));
    setVideoInput('');
  };

  const updateVideoCaption = (index, caption) => {
    const newVideos = [...formData.videos];
    newVideos[index] = { ...newVideos[index], caption };
    setFormData(prev => ({ ...prev, videos: newVideos }));
  };

  const removeVideo = (index) => {
    setFormData(prev => ({
      ...prev,
      videos: prev.videos.filter((_, i) => i !== index)
    }));
  };

  const getEventTitle = (eventId) => {
    const event = events.find(e => e.id === eventId);
    return event?.title || 'N/A';
  };

  const getYoutubeThumbnail = (url) => {
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)/);
    return match ? `https://img.youtube.com/vi/${match[1]}/mqdefault.jpg` : null;
  };

  // Filtered galleries
  const filteredGalleries = filterEventId === 'all'
    ? galleries
    : galleries.filter(g => g.event_id === filterEventId);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="galleries-admin-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mb-2">Gallery</h1>
          <p className="text-muted-foreground">Gestisci le gallery di immagini e video</p>
        </div>

        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="rounded-full bg-primary hover:bg-primary/90" data-testid="create-gallery-btn">
              <Plus size={18} className="mr-2" />
              Nuova Gallery
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-card border-white/10 max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editingGallery ? 'Modifica Gallery' : 'Nuova Gallery'}
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Titolo *</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    required
                    className="bg-white/5 border-white/10"
                    data-testid="input-gallery-title"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Evento *</Label>
                  <Select
                    value={formData.event_id}
                    onValueChange={(value) => setFormData({ ...formData, event_id: value })}
                  >
                    <SelectTrigger className="bg-white/5 border-white/10" data-testid="select-gallery-event">
                      <SelectValue placeholder="Seleziona evento" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-white/10">
                      {events.map((event) => (
                        <SelectItem key={event.id} value={event.id}>
                          {event.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Descrizione</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="bg-white/5 border-white/10 min-h-[80px]"
                  data-testid="input-gallery-description"
                />
              </div>

              {/* Hero Image */}
              <div className="space-y-2">
                <Label>Immagine Hero</Label>
                <ImageUpload
                  value={formData.hero_image}
                  onChange={(url) => setFormData({ ...formData, hero_image: url })}
                />
              </div>

              {/* Images Section */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Image size={18} className="text-primary" />
                  <Label>Immagini Gallery</Label>
                </div>
                
                <ImageUpload
                  value=""
                  onChange={(url) => addImage(url)}
                  buttonText="Aggiungi immagine"
                />

                {formData.images.length > 0 && (
                  <div className="grid grid-cols-3 gap-3 mt-3">
                    {formData.images.map((image, index) => (
                      <div key={`img-${image.url.slice(-20)}`} className="relative group">
                        <img 
                          src={image.url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${image.url}` : image.url}
                          alt={`Gallery ${index + 1}`}
                          className="w-full h-24 object-cover rounded-lg"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute top-1 right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X size={12} />
                        </button>
                        <Input
                          placeholder="Didascalia"
                          value={image.caption || ''}
                          onChange={(e) => updateImageCaption(index, e.target.value)}
                          className="mt-1 text-xs bg-white/5 border-white/10"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Videos Section */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Video size={18} className="text-primary" />
                  <Label>Video (YouTube o upload)</Label>
                </div>
                
                <div className="flex gap-2">
                  <Input
                    value={videoInput}
                    onChange={(e) => setVideoInput(e.target.value)}
                    placeholder="Inserisci URL YouTube o video caricato..."
                    className="bg-white/5 border-white/10 flex-1"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addVideo();
                      }
                    }}
                  />
                  <Button type="button" variant="outline" onClick={addVideo}>
                    <Plus size={16} />
                  </Button>
                </div>

                {formData.videos.length > 0 && (
                  <div className="space-y-2 mt-3">
                    {formData.videos.map((video, index) => (
                      <div key={`vid-${video.url.slice(-20)}`} className="flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-white/10">
                        {video.type === 'youtube' && getYoutubeThumbnail(video.url) ? (
                          <img 
                            src={getYoutubeThumbnail(video.url)}
                            alt="Video thumbnail"
                            className="w-20 h-12 object-cover rounded"
                          />
                        ) : (
                          <div className="w-20 h-12 bg-primary/20 rounded flex items-center justify-center">
                            <Video size={20} className="text-primary" />
                          </div>
                        )}
                        <div className="flex-1">
                          <p className="text-xs text-muted-foreground truncate max-w-xs">{video.url}</p>
                          <span className="text-xs px-2 py-0.5 rounded bg-primary/20 text-primary">
                            {video.type === 'youtube' ? 'YouTube' : 'Upload'}
                          </span>
                        </div>
                        <Input
                          placeholder="Didascalia"
                          value={video.caption || ''}
                          onChange={(e) => updateVideoCaption(index, e.target.value)}
                          className="w-32 text-xs bg-white/5 border-white/10"
                        />
                        <button
                          type="button"
                          onClick={() => removeVideo(index)}
                          className="text-destructive hover:text-destructive/80"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Submit */}
              <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                  Annulla
                </Button>
                <Button type="submit" className="bg-primary hover:bg-primary/90" data-testid="submit-gallery-btn">
                  {editingGallery ? 'Aggiorna' : 'Crea'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Filter by Event */}
      <div className="flex items-center gap-4">
        <Label className="text-sm font-medium">Filtra per evento:</Label>
        <Select value={filterEventId} onValueChange={setFilterEventId}>
          <SelectTrigger className="w-[300px] bg-white/5 border-white/10" data-testid="filter-gallery-event">
            <SelectValue placeholder="Tutti gli eventi" />
          </SelectTrigger>
          <SelectContent className="bg-card border-white/10">
            <SelectItem value="all">Tutti gli eventi</SelectItem>
            {events.map((event) => (
              <SelectItem key={event.id} value={event.id}>
                {event.title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {filterEventId !== 'all' && (
          <span className="text-sm text-muted-foreground">
            {filteredGalleries.length} gallery trovate
          </span>
        )}
      </div>

      {/* Table */}
      <div className="glass rounded-xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-white/5 hover:bg-transparent">
              <TableHead>Titolo</TableHead>
              <TableHead>Evento</TableHead>
              <TableHead>Immagini</TableHead>
              <TableHead>Video</TableHead>
              <TableHead className="text-right">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredGalleries.map((gallery) => (
              <TableRow key={gallery.id} className="border-white/5" data-testid={`gallery-row-${gallery.id}`}>
                <TableCell className="font-medium">
                  <div className="flex items-center gap-3">
                    {gallery.hero_image ? (
                      <img 
                        src={gallery.hero_image.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${gallery.hero_image}` : gallery.hero_image}
                        alt={gallery.title}
                        className="w-10 h-10 rounded object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded bg-primary/20 flex items-center justify-center">
                        <Image size={16} className="text-primary" />
                      </div>
                    )}
                    {gallery.title}
                  </div>
                </TableCell>
                <TableCell>{getEventTitle(gallery.event_id)}</TableCell>
                <TableCell>{gallery.images?.length || 0}</TableCell>
                <TableCell>{gallery.videos?.length || 0}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-2">
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => window.open(`/gallery/${gallery.slug}`, '_blank')}
                      data-testid={`view-gallery-${gallery.id}`}
                    >
                      <ExternalLink size={16} />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => openEditDialog(gallery)}
                      data-testid={`edit-gallery-${gallery.id}`}
                    >
                      <Pencil size={16} />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => handleDelete(gallery.id)}
                      data-testid={`delete-gallery-${gallery.id}`}
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}

            {filteredGalleries.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-12">
                  <Image className="mx-auto text-muted-foreground mb-4" size={32} />
                  <p className="text-muted-foreground">
                    {filterEventId !== 'all' ? 'Nessuna gallery per questo evento' : 'Nessuna gallery trovata'}
                  </p>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
